import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "../../i18n/LanguageContext";
import forceMutedAutoplay from "../utils/forceMutedAutoplay";
import "./HomeQapex.css";
import { Arrow, Icons, QapexNav, QapexFooter, Certificate, useReveal } from "./QapexChrome";
import heroVideo from "../../assets/Home/hero.mp4";
import heroVideoMobile from "../../assets/Home/hero-mobile.mp4";
import heroPoster from "../../assets/Home/hero-poster.jpg";
import heroPosterMobile from "../../assets/Home/hero-poster-mobile.jpg";
import istqb from "../../assets/Home/istqb.png";
import quackVideo from "../../assets/Home/quack.mp4";
import quackPoster from "../../assets/Home/quack-poster.jpg";
import poste from "../../assets/Home/partner/poste.png";
import mastercard from "../../assets/Home/partner/mastercard.png";
import paramount from "../../assets/Home/partner/paramount.png";
import sopra from "../../assets/Home/partner/sopra.png";
import aci from "../../assets/Home/partner/aci.png";
import avanti from "../../assets/Home/partner/avanti.png";
import deptra from "../../assets/Home/partner/deptra.png";
import clariter from "../../assets/Home/partner/clariter.png";
import pmc from "../../assets/Home/partner/pmc.png";
import zapaygo from "../../assets/Home/partner/zapaygo.png";

/**
 * Home del sito, riportata dalla bozza (repo sito-bozza, index.html).
 * Barra e footer sono quelli di QapexChrome: in App.js la rotta "/" non usa quelli generici.
 * Gli stili stanno in HomeQapex.css, tutti sotto la classe .qx.
 */

// Stesso endpoint del form della pagina Contatti
const GAS_URL =
  "https://script.google.com/macros/s/AKfycbzzmPdDsbNOX4c09jKLgAPgLGII5Oy3VpLoig00ykRKgJhweVZNPtkAAOOF-t6ADjMejQ/exec";

const MOBILE = "(max-width:720px)";

const PARTNERS = [
  { src: poste, alt: "Poste Italiane", h: 26 },
  { src: mastercard, alt: "Mastercard", h: 46 },
  { src: paramount, alt: "Paramount", h: 46 },
  { src: sopra, alt: "Sopra Steria", h: 24 },
  { src: aci, alt: "ACI", h: 34 },
  { src: avanti, alt: "Avanti West Coast", h: 46 },
  { src: deptra, alt: "Department for Transport", h: 46 },
  { src: clariter, alt: "Clariter", h: 30 },
  { src: pmc, alt: "ParkingMyCar", h: 46 },
  { src: zapaygo, alt: "Zapaygo", h: 28 },
];

const SERVICE_ICONS = ["i-cloud", "i-flow", "i-server", "i-devices", "i-gauge", "i-code"];

// Ruolo e testo di ogni recensione stanno nei dizionari (home.refs.items.<id>)
const REVIEWS = [
  { id: "stefanetti", initials: "LS", name: "Lorenzo Stefanetti" },
  { id: "pettini", initials: "SP", name: "Stefano Pettini" },
  { id: "innamorati", initials: "AI", name: "Andrea Innamorati" },
  { id: "picciotto", initials: "MP", name: "Mirko Picciotto" },
  { id: "lombardozzi", initials: "FL", name: "Fiammetta Lombardozzi" },
];

const PREFIXES = ["🇮🇹 +39", "🇪🇸 +34", "🇬🇧 +44", "🇺🇸 +1"];

const EMPTY_FORM = { nome: "", cognome: "", email: "", prefisso: PREFIXES[0], telefono: "", servizio: "", testo: "", privacy: false };

const IsoSeal = ({ code }) => (
  <svg className="iso" viewBox="0 0 110 110" aria-hidden="true">
    <circle cx="55" cy="55" r="52" fill="none" stroke="#106CA5" strokeWidth="3" />
    <circle cx="55" cy="55" r="42" fill="none" stroke="#106CA5" strokeWidth="1.5" />
    <text x="55" y="52" textAnchor="middle" fontFamily="Poppins" fontWeight="700" fontSize="20" fill="#106CA5">ISO</text>
    <text x="55" y="74" textAnchor="middle" fontFamily="Poppins" fontWeight="700" fontSize="18" fill="#106CA5">{code}</text>
  </svg>
);

// Referenze: carosello centrato che avanza da solo (pausa al passaggio del mouse).
// Le card sono ripetute tre volte per lo scorrimento infinito: si lavora sempre sulla copia centrale.
function References({ t }) {
  const viewRef = useRef(null);
  const trackRef = useRef(null);
  const dotsRef = useRef(null);
  const n = REVIEWS.length;

  useEffect(() => {
    const rview = viewRef.current, track = trackRef.current;
    const cards = [...track.children], dots = [...dotsRef.current.children];
    let cur = n, timer, swiped = false, sx = null;

    const go = (k, anim = true) => {
      cur = k;
      track.classList.toggle("jump", !anim);
      const c = cards[cur];
      track.style.transform = `translateX(${rview.clientWidth / 2 - (c.offsetLeft + c.offsetWidth / 2)}px)`;
      cards.forEach((x, j) => x.classList.toggle("on", j === cur));
      dots.forEach((d, j) => d.classList.toggle("on", j === cur % n));
      if (!anim) { void track.offsetHeight; track.classList.remove("jump"); }
    };
    const reduce = matchMedia("(prefers-reduced-motion:reduce)").matches;
    const play = () => { clearInterval(timer); if (!reduce) timer = setInterval(() => go(cur + 1), 5000); };
    const pause = () => clearInterval(timer);

    const onEnd = (e) => {
      if (e.target !== track) return;
      if (cur >= 2 * n) go(cur - n, false); else if (cur < n) go(cur + n, false);
    };
    const cardClicks = cards.map((c, j) => () => { if (swiped) { swiped = false; return; } if (j !== cur) { go(j); play(); } });
    const dotClicks = dots.map((d, k) => () => { go(n + k); play(); });
    const onDown = (e) => { sx = e.clientX; };
    const onUp = (e) => {
      swiped = sx !== null && Math.abs(e.clientX - sx) > 40;
      if (swiped) { go(cur + (e.clientX < sx ? 1 : -1)); play(); }
      sx = null;
    };
    const onResize = () => go(cur, false);

    track.addEventListener("transitionend", onEnd);
    rview.addEventListener("mouseenter", pause);
    rview.addEventListener("mouseleave", play);
    rview.addEventListener("pointerdown", onDown);
    rview.addEventListener("pointerup", onUp);
    cards.forEach((c, j) => c.addEventListener("click", cardClicks[j]));
    dots.forEach((d, k) => d.addEventListener("click", dotClicks[k]));
    window.addEventListener("resize", onResize);
    window.addEventListener("load", onResize);
    go(cur, false); play();

    return () => {
      clearInterval(timer);
      track.removeEventListener("transitionend", onEnd);
      rview.removeEventListener("mouseenter", pause);
      rview.removeEventListener("mouseleave", play);
      rview.removeEventListener("pointerdown", onDown);
      rview.removeEventListener("pointerup", onUp);
      cards.forEach((c, j) => c.removeEventListener("click", cardClicks[j]));
      dots.forEach((d, k) => d.removeEventListener("click", dotClicks[k]));
      window.removeEventListener("resize", onResize);
      window.removeEventListener("load", onResize);
    };
  }, [n]);

  return (
    <section className="refs" id="referenze">
      <div className="wrap">
        <div className="split reveal" style={{ marginBottom: 48 }}>
          <div><div className="eyebrow">{t("home.refs.eyebrow")}</div><h2 style={{ marginBottom: 0 }}>{t("home.refs.title")}</h2></div>
        </div>
      </div>
      <div className="rview reveal" ref={viewRef} aria-roledescription={t("home.refs.carousel")}>
        <div className="rtrack" ref={trackRef}>
          {[0, 1, 2].flatMap((copy) =>
            REVIEWS.map((r) => (
              <figure className="ref" key={`${copy}-${r.id}`} aria-hidden={copy !== 1 ? "true" : undefined}>
                <figcaption className="who"><div className="av">{r.initials}</div><div><b>{r.name}</b><span>{t(`home.refs.items.${r.id}.role`)}</span></div></figcaption>
                <div className="stars">★★★★★</div>
                <blockquote className="q">"{t(`home.refs.items.${r.id}.text`)}"</blockquote>
              </figure>
            ))
          )}
        </div>
      </div>
      <div className="rdots" ref={dotsRef}>
        {REVIEWS.map((r, k) => <button type="button" key={r.id} aria-label={t("home.refs.goTo", { n: k + 1 })} />)}
      </div>
    </section>
  );
}

function ContactForm({ t, servizio, setServizio }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const options = t("contactForm.options");

  const set = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      // Il backend riceve gli stessi campi del form della pagina Contatti
      const params = new URLSearchParams({
        nome: form.nome,
        cognome: form.cognome,
        email: form.email,
        telefono: form.telefono ? `${form.prefisso.split(" ")[1]} ${form.telefono}` : "",
        messaggio: `${servizio}\n\n${form.testo}`,
      });
      const res = await fetch(GAS_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8" },
        body: params.toString(),
      });
      const json = await res.json();
      if (!json.ok) throw new Error(json.error || "Invio fallito");
      setSent(true);
      setForm(EMPTY_FORM);
      setServizio("");
    } catch (err) {
      console.error(err);
      alert(t("contactForm.error"));
    } finally {
      setSending(false);
    }
  };

  return (
    <form className="reveal" onSubmit={handleSubmit}>
      <h3>{t("home.contact.formTitle")}</h3>
      <div className="frow">
        <div className="field"><label htmlFor="qx-nome">{t("home.contact.firstName")} *</label><input id="qx-nome" name="nome" value={form.nome} onChange={set} placeholder={t("home.contact.firstNamePlaceholder")} autoComplete="given-name" maxLength={25} required /></div>
        <div className="field"><label htmlFor="qx-cognome">{t("home.contact.lastName")} *</label><input id="qx-cognome" name="cognome" value={form.cognome} onChange={set} placeholder={t("home.contact.lastNamePlaceholder")} autoComplete="family-name" maxLength={25} required /></div>
      </div>
      <div className="frow">
        <div className="field"><label htmlFor="qx-email">{t("home.contact.email")} *</label><input id="qx-email" name="email" type="email" value={form.email} onChange={set} placeholder={t("home.contact.emailPlaceholder")} autoComplete="email" maxLength={50} required /></div>
        <div className="field"><label htmlFor="qx-tel">{t("home.contact.phone")}</label>
          <div className="phone">
            <select name="prefisso" value={form.prefisso} onChange={set} aria-label={t("home.contact.phonePrefix")}>
              {PREFIXES.map((p) => <option key={p}>{p}</option>)}
            </select>
            <input id="qx-tel" name="telefono" type="tel" value={form.telefono} onChange={set} placeholder={t("home.contact.phonePlaceholder")} autoComplete="tel-national" maxLength={25} />
          </div>
        </div>
      </div>
      <div className="field"><label htmlFor="qx-servizio">{t("home.contact.service")} *</label>
        <select id="qx-servizio" value={servizio} onChange={(e) => setServizio(e.target.value)} required>
          <option value="" disabled>{t("home.contact.selectService")}</option>
          {Object.entries(options).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
      </div>
      <div className="field"><label htmlFor="qx-messaggio">{t("home.contact.message")} *</label><textarea id="qx-messaggio" name="testo" value={form.testo} onChange={set} placeholder={t("home.contact.messagePlaceholder")} required /></div>
      <label className="check">
        <input type="checkbox" name="privacy" checked={form.privacy} onChange={set} required />
        <span>
          {t.rich("contactForm.privacy", {
            link: (chunk) => <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">{chunk}</a>,
          })}
        </span>
      </label>
      <button type="submit" className="btn btn-dark submit" disabled={sending} aria-busy={sending ? "true" : "false"}>
        {sending ? t("contactForm.sending") : t("contactForm.submit")}
      </button>
      {sent && <div className="msg" style={{ display: "block" }}>{t("contactForm.success")}</div>}
    </form>
  );
}

function VideoModal({ video, onClose, t }) {
  const [open, setOpen] = useState(false);
  const playerRef = useRef(null);
  const closeRef = useRef(null);

  useEffect(() => {
    if (!video) return;
    const raf = requestAnimationFrame(() => setOpen(true));
    document.body.style.overflow = "hidden";
    playerRef.current.play().catch(() => {});
    closeRef.current.focus();
    const onKey = (e) => e.key === "Escape" && close();
    document.addEventListener("keydown", onKey);
    return () => { cancelAnimationFrame(raf); document.removeEventListener("keydown", onKey); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [video]);

  const close = () => {
    setOpen(false);
    playerRef.current.pause();
    document.body.style.overflow = "";
    setTimeout(onClose, 350);
  };

  return (
    <div className={`vmodal${open ? " open" : ""}`} role="dialog" aria-modal="true" aria-label={t("home.video.dialog")} hidden={!video}
      onClick={(e) => e.target === e.currentTarget && close()}>
      <div className="vbox">
        <div className="vtitle">{video?.title}</div>
        <button type="button" className="vclose" ref={closeRef} aria-label={t("home.video.close")} onClick={close}>
          <svg viewBox="0 0 24 24"><path d="M6 6l12 12M18 6L6 18" /></svg>
        </button>
        <video ref={playerRef} src={video?.src} poster={video?.poster} controls playsInline preload="none" />
      </div>
    </div>
  );
}

export default function HomeQapex() {
  const { t } = useTranslation();
  const { hash, state } = useLocation();
  const rootRef = useRef(null);
  const heroRef = useRef(null);
  const playRef = useRef(null);
  const [video, setVideo] = useState(null);
  // Il servizio può arrivare preselezionato da un'altra pagina (es. Certificazione Qapex)
  const [servizio, setServizio] = useState(state?.servizio || "");

  // Video hero: versione mobile sotto i 720px
  useEffect(() => {
    const v = heroRef.current;
    const mobile = matchMedia(MOBILE).matches;
    v.poster = mobile ? heroPosterMobile : heroPoster;
    v.src = mobile ? heroVideoMobile : heroVideo;
    forceMutedAutoplay(v);
  }, []);

  // Arrivando da un'altra pagina con /#sezione si scorre alla sezione
  useEffect(() => {
    if (hash) document.getElementById(hash.slice(1))?.scrollIntoView();
  }, [hash]);

  useReveal(rootRef);

  // Card servizi: il riempimento parte (e si ritira) dal punto del mouse

  const fillFrom = (e) => {
    const c = e.currentTarget, r = c.getBoundingClientRect();
    c.style.setProperty("--x", `${e.clientX - r.left}px`);
    c.style.setProperty("--y", `${e.clientY - r.top}px`);
  };

  const closeVideo = () => { setVideo(null); playRef.current && playRef.current.focus(); };


  return (
    <div className="qx" ref={rootRef}>
      <Icons />

      <QapexNav t={t} home />

      {/* HERO */}
      <section className="hero" id="top">
        <video ref={heroRef} autoPlay muted loop playsInline preload="auto" />
        <div className="shade" />
        <div className="content">
          <h1>Why do it<br /><b>manually?</b></h1>
          <div className="side">
            <p>{t("home.hero.text")}</p>
            <div className="ctas">
              <a href="#servizi" className="btn btn-primary">{t("home.hero.ctaServices")} <Arrow /></a>
              <a href="#contatti" className="btn btn-ghost">{t("home.hero.ctaTalk")}</a>
            </div>
          </div>
        </div>
        <div className="scroll" />
      </section>

      {/* PARTNER: due giri di loghi per il loop continuo */}
      <section className="partners" aria-label={t("home.partners.aria")}>
        <div className="label">{t("home.partners.label")}</div>
        <div className="marquee">
          {[0, 1].flatMap((copy) =>
            PARTNERS.map((p) => (
              <img key={`${copy}-${p.alt}`} src={p.src} alt={copy ? "" : p.alt} aria-hidden={copy ? "true" : undefined} style={{ height: p.h }} />
            ))
          )}
        </div>
      </section>

      {/* MODELLO */}
      <section id="modello">
        <div className="wrap">
          <div className="split reveal">
            <div><div className="eyebrow">{t("home.model.eyebrow")}</div><h2>{t("home.model.title")}</h2></div>
            <p className="lead">{t.rich("home.model.lead", { b: (c) => <b style={{ color: "var(--ink)" }}>{c}</b> })}</p>
          </div>
          <div className="model-grid reveal">
            <div className="mcard">
              <div className="ic"><svg><use href="#i-chip" /></svg></div>
              <h3>{t("home.model.agentic.title")}</h3>
              <p>{t("home.model.agentic.text")}</p>
            </div>
            <div className="plus">+</div>
            <div className="mcard dark">
              <div className="ic"><svg><use href="#i-shield" /></svg></div>
              <h3>{t("home.model.qa.title")}</h3>
              <p>{t("home.model.qa.text")}</p>
            </div>
          </div>
        </div>
      </section>

      {/* SERVIZI */}
      <section className="services" id="servizi">
        <div className="wrap">
          <div className="center reveal">
            <div className="eyebrow">{t("home.services.eyebrow")}</div>
            <h2>{t("home.services.title")}</h2>
            <p className="lead">{t("home.services.lead")}</p>
          </div>
          <div className="sgrid">
            {t("home.services.items").map((s, i) => (
              <article className="scard reveal" key={SERVICE_ICONS[i]} onMouseEnter={fillFrom} onMouseLeave={fillFrom}>
                <div className="ic"><svg><use href={`#${SERVICE_ICONS[i]}`} /></svg></div><h3>{s.title}</h3><p>{s.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* QA + CERTIFICAZIONE */}
      <section className="qa" id="quality-assurance">
        <div className="glow" />
        <div className="wrap">
          <div className="split reveal">
            <div><div className="eyebrow">{t("home.qa.eyebrow")}</div><h2>{t("home.qa.title")} <span>{t("home.qa.titleHighlight")}</span></h2></div>
            <p className="lead">{t("home.qa.lead")}</p>
          </div>
          <div className="qa-cols">
            {t("home.qa.cards").map((c) => (
              <div className="qcard reveal" key={c.title}>
                <div className="tag">{c.tag}</div>
                <h3>{c.title}</h3>
                <p>{c.text}</p>
                <div className="chips">{c.chips.map((ch) => <span className="chip" key={ch}>{ch}</span>)}</div>
              </div>
            ))}
          </div>
          <div className="cert-band compact reveal">
            <div className="cert-head">
              <div className="eyebrow">{t("home.qa.cert.eyebrow")}</div>
              <h3>{t("home.qa.cert.title")}<br />{t("home.qa.cert.titleLine2")}</h3>
            </div>
            <Certificate t={t} />
            <div className="cert-cta">
              <Link to="/certificazione-qapex" className="btn btn-white">{t("home.qa.cert.discover")} <Arrow /></Link>
            </div>
          </div>
        </div>
      </section>

      {/* COME LAVORIAMO */}
      <section id="processo">
        <div className="wrap">
          <div className="split reveal">
            <div><div className="eyebrow">{t("home.process.eyebrow")}</div><h2>{t("home.process.title")}</h2></div>
            <p className="lead">{t("home.process.lead")}</p>
          </div>
          <div className="pgrid">
            {t("home.process.steps").map((s, i) => (
              <div className="pstep reveal" key={s.phase}><div className="n">{i + 1}</div><div className="phase">{s.phase}</div><h3>{s.title}</h3><p>{s.text}</p></div>
            ))}
          </div>
        </div>
      </section>

      {/* PRODOTTI */}
      <section className="products" id="prodotti">
        <div className="wrap">
          <div className="split reveal">
            <div><div className="eyebrow">{t("home.products.eyebrow")}</div><h2>{t("home.products.title")}</h2></div>
            <a href="#contatti" className="btn btn-dark">{t("home.products.demo")}</a>
          </div>
          <div className="pr-grid">
            <article className="prod alt quack reveal">
              <div className="shot">
                <div className="win qk" aria-hidden="true">
                  <div className="qk-side">
                    <div className="qk-brand"><svg viewBox="0 0 64 64"><rect width="64" height="64" rx="14" fill="#0a77ae" /><path d="M22 24a8 8 0 1 1 16 0v3h8l-6 6h-2v6a11 11 0 0 1-11 11H16a13 13 0 0 0 13-13v-4a9 9 0 0 1-7-9z" fill="#fff" /><circle cx="26" cy="23" r="2" fill="#0a77ae" /></svg>QUACK</div>
                    <div className="qk-group">{t("home.products.quack.tools")}</div>
                    <div className="qk-item on"><svg viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><circle cx="11.5" cy="14.5" r="2.5" /><path d="m13.3 16.3 1.7 1.7" /></svg>Analysis AI</div>
                    <div className="qk-item"><svg viewBox="0 0 24 24"><rect x="8" y="2" width="8" height="4" rx="1" /><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" /><path d="m9 14 2 2 4-4" /></svg>Testbook AI</div>
                  </div>
                  <div className="qk-main">
                    <div className="qk-title">Analysis AI</div>
                    <div className="qk-steps">{t("home.products.quack.steps").map((s, i) => <span key={s} className={i === 0 ? "on" : undefined}>{s}</span>)}</div>
                    <div className="qk-file"><svg viewBox="0 0 24 24"><path d="m22 8-6 4 6 4V8z" /><rect x="2" y="6" width="14" height="12" rx="2" /></svg><span>kickoff-cliente.mp4</span><em>48 MB</em></div>
                    <div className="qk-job"><span>{t("home.products.quack.running")}</span><div className="qk-prog"><i /></div></div>
                    <div className="qk-out"><span className="qk-pill">analisi-funzionale.docx</span><span className="qk-pill">backlog.pdf</span></div>
                  </div>
                </div>
                <button type="button" className="qk-play" ref={playRef} aria-label={t("home.products.quack.play")}
                  onClick={() => setVideo({ src: quackVideo, poster: quackPoster, title: "QUACK · Analysis AI" })}>
                  <svg viewBox="0 0 24 24"><path d="M8 5.5v13l11-6.5z" /></svg>
                </button>
              </div>
              <div className="body">
                <div className="badge">QUACK · ANALYSIS AI</div>
                <h3>Quack</h3>
                <p>{t("home.products.quack.text")}</p>
                <a href="https://quackbyqapex.com/" className="link" target="_blank" rel="noopener noreferrer">{t("home.products.quack.cta")} <Arrow /></a>
              </div>
            </article>
            <article className="prod reveal">
              <div className="shot">
                <div className="win" aria-hidden="true">
                  <div className="dots"><i /><i /><i /></div>
                  <div className="row"><div className="cell h" style={{ flex: 0.5 }} /><div className="cell h" /><div className="cell h" style={{ flex: 2 }} /><div className="cell h" style={{ flex: 0.7 }} /></div>
                  {["PASS", "PASS", "REVIEW", "PASS"].map((s, i) => (
                    <div className="row" key={i}><div className="cell" style={{ flex: 0.5 }} /><div className="cell" /><div className="cell" style={{ flex: 2 }} /><div className={`ok${s === "REVIEW" ? " rv" : ""}`}>{s}</div></div>
                  ))}
                </div>
              </div>
              <div className="body">
                <div className="badge">SUBQ</div>
                <h3>SubQ</h3>
                <p>{t("home.products.subq.text")}</p>
                <a href="https://subq.it" className="link" target="_blank" rel="noopener noreferrer">{t("home.products.subq.cta")} <Arrow /></a>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* CERTIFICAZIONI */}
      <section id="certificazioni">
        <div className="wrap">
          <div className="center reveal">
            <div className="eyebrow">{t("home.certs.eyebrow")}</div>
            <h2>{t("home.certs.title")}</h2>
            <p className="lead">{t("home.certs.lead")}</p>
          </div>
          <div className="cgrid">
            <div className="ccard main reveal">
              <img src={istqb} alt="ISTQB" style={{ height: 110, width: "auto", alignSelf: "flex-start", objectFit: "contain" }} />
              <div><span className="st on">{t("home.certs.active")}</span><h3 style={{ marginTop: 12 }}>ISTQB® Silver Partner</h3><p>{t("home.certs.istqb")}</p></div>
            </div>
            <div className="ccard reveal">
              <IsoSeal code="9001" />
              <div><span className="st plan">{t("home.certs.planned")}</span><h3 style={{ marginTop: 12 }}>ISO 9001</h3><p>{t("home.certs.iso9001")}</p></div>
            </div>
            <div className="ccard reveal">
              <IsoSeal code="27001" />
              <div><span className="st plan">{t("home.certs.planned")}</span><h3 style={{ marginTop: 12 }}>ISO/IEC 27001</h3><p>{t("home.certs.iso27001")}</p></div>
            </div>
          </div>
        </div>
      </section>

      <References t={t} />

      {/* CONTATTI */}
      <section id="contatti">
        <div className="wrap cwrap">
          <div className="reveal">
            <div className="eyebrow">{t("home.contact.eyebrow")}</div>
            <h2>{t("home.contact.title")}</h2>
            <p className="lead">{t("home.contact.lead")}</p>
            <div className="info">
              <div className="it"><div className="ic"><svg><use href="#i-mail" /></svg></div><div><b>{t("home.contact.email")}</b><a href="mailto:info@qapex.it">info@qapex.it</a></div></div>
              <div className="it"><div className="ic"><svg><use href="#i-pin" /></svg></div><div><b>{t("home.contact.office")}</b><span>{t("home.contact.officeValue")}</span></div></div>
              <div className="it"><div className="ic"><svg><use href="#i-doc" /></svg></div><div><b>{t("home.contact.vat")}</b><span>IT03818350542</span></div></div>
            </div>
            <div className="social">
              <a href="https://www.linkedin.com/company/qapexit/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn"><svg viewBox="0 0 24 24" fill="#fff"><path d="M4.98 3.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5zM3 9.5h4V21H3zM9.5 9.5h3.8v1.6h.1c.5-1 1.8-2 3.8-2 4 0 4.8 2.6 4.8 6V21h-4v-5.2c0-1.3 0-2.9-1.8-2.9s-2 1.4-2 2.8V21h-4z" /></svg></a>
              <a href="https://www.instagram.com/qapex_srl/" target="_blank" rel="noopener noreferrer" aria-label="Instagram"><svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="5" /><circle cx="12" cy="12" r="4" /><circle cx="17.5" cy="6.5" r="1" fill="#fff" /></svg></a>
            </div>
          </div>
          <ContactForm t={t} servizio={servizio} setServizio={setServizio} />
        </div>
      </section>


      <QapexFooter t={t} home />

      <VideoModal video={video} onClose={closeVideo} t={t} />
    </div>
  );
}
